import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FleetManagementState,
  getInitialFleetManagementState,
} from './fleet-management-slice-definitions';
import {
  createNewFleetDashboard,
  createNewFleetDataSet,
  deleteDashboardById,
  deleteDataSetById,
  getFleetDashboards,
  getFleetData,
  getFleetDataByFilters,
  getFleetDataSets,
  getInsights,
  isLiveQueryKey,
  LIVE_QUERY_CACHE_LIMIT,
  saveFleetDashboard,
  saveFleetDataSet,
} from './fleet-management-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { updateDataList } from 'utils/generic-data-list-handlers';
import RestSet from '@common/interfaces/api/rest-set';

const initialFleetManagementState = getInitialFleetManagementState();

/**
 * Every filter combination asked for stays cached, so past the limit the oldest answers go. Entries
 * still loading are never dropped, and saved datasets (keyed by their id) are left alone.
 */
const evictOldLiveQueries = (state: FleetManagementState) => {
  const answered = state.live.filter(
    (l) => isLiveQueryKey(l.id) && l.rest.getStatus !== HttpFetchStatus.Loading,
  );
  const excess = state.live.filter((l) => isLiveQueryKey(l.id)).length - LIVE_QUERY_CACHE_LIMIT;
  if (excess <= 0) return;

  const dropped = new Set(
    answered
      .sort((a, b) => (a.fetchedAt ?? 0) - (b.fetchedAt ?? 0))
      .slice(0, excess)
      .map((l) => l.id),
  );
  state.live = state.live.filter((l) => !dropped.has(l.id));
};

/** A live query started: the last answer stays until the new one lands, so a refetch dims the page. */
const startLiveQuery = (state: FleetManagementState, id: string) => {
  const entry = state.live.find((l) => l.id === id);
  if (entry != null) {
    entry.rest.getStatus = HttpFetchStatus.Loading;
    entry.rest.getError = null;
  } else {
    state.live.push({
      id,
      loadTime: null,
      data: null,
      rest: { getStatus: HttpFetchStatus.Loading, getError: null },
    });
  }
};

const finishLiveQuery = (
  state: FleetManagementState,
  id: string,
  payload: { loadTime: number; data: unknown },
) => {
  const entry = state.live.find((l) => l.id === id);
  if (entry != null) {
    entry.rest.getStatus = HttpFetchStatus.Success;
    entry.rest.getError = null;
    entry.loadTime = payload.loadTime;
    entry.fetchedAt = Date.now();
    entry.data = payload.data;
  }
  evictOldLiveQueries(state);
};

const failLiveQuery = (
  state: FleetManagementState,
  id: string,
  error: FleetManagementState['live'][number]['rest']['getError'] | undefined,
) => {
  const entry = state.live.find((l) => l.id === id);
  if (entry != null) {
    entry.rest.getStatus = HttpFetchStatus.Failed;
    entry.rest.getError = error ?? null;
  }
};

const fleetManagementSlice = createSlice({
  name: 'fleet-management',
  initialState: initialFleetManagementState,
  reducers: {
    setCurrentDashboardById(state, action: PayloadAction<string>) {
      state.dashboards.selectedId = action.payload;
    },
    patchDashboardsRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.dashboards.rest = { ...state.dashboards.rest, ...action.payload };
    },
    patchDataSetsRestState(state, action: PayloadAction<Partial<RestSet>>) {
      state.datasets.rest = { ...state.datasets.rest, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getFleetDashboards.pending, (state, action) => {
      state.dashboards.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getFleetDashboards.fulfilled, (state, action) => {
      state.dashboards.rest.getStatus = HttpFetchStatus.Success;
      state.dashboards.data = action.payload;
    });
    builder.addCase(getFleetDashboards.rejected, (state, action) => {
      state.dashboards.rest.getStatus = HttpFetchStatus.Failed;
      state.dashboards.rest.getError = action.payload ?? null;
    });
    builder.addCase(createNewFleetDashboard.pending, (state, action) => {
      state.dashboards.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createNewFleetDashboard.fulfilled, (state, action) => {
      state.dashboards.rest.postStatus = HttpFetchStatus.Success;
      state.dashboards.data = [...(state.dashboards.data ?? []), action.payload];
    });
    builder.addCase(createNewFleetDashboard.rejected, (state, action) => {
      state.dashboards.rest.postStatus = HttpFetchStatus.Failed;
      state.dashboards.rest.postError = action.payload ?? null;
    });
    builder.addCase(saveFleetDashboard.pending, (state, action) => {
      state.dashboards.rest.patchStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(saveFleetDashboard.fulfilled, (state, action) => {
      state.dashboards.rest.patchStatus = HttpFetchStatus.Success;
      state.dashboards.data = updateDataList(action.payload, state.dashboards.data);
    });
    builder.addCase(saveFleetDashboard.rejected, (state, action) => {
      state.dashboards.rest.patchStatus = HttpFetchStatus.Failed;
      state.dashboards.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteDashboardById.pending, (state, action) => {
      state.dashboards.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteDashboardById.fulfilled, (state, action) => {
      state.dashboards.rest.deleteStatus = HttpFetchStatus.Success;
      const deletedDashboardIndex = state.dashboards.data?.findIndex(
        (d) => d.id === action.payload,
      );
      if (deletedDashboardIndex != null && deletedDashboardIndex !== -1) {
        state.dashboards.data!.splice(deletedDashboardIndex, 1);
      }
    });
    builder.addCase(deleteDashboardById.rejected, (state, action) => {
      state.dashboards.rest.deleteStatus = HttpFetchStatus.Failed;
      state.dashboards.rest.deleteError = action.payload ?? null;
    });

    builder.addCase(getFleetData.pending, (state, action) => {
      const existingLiveData = state.live.find((l) => l.id === action.meta.arg);
      if (existingLiveData != null) {
        existingLiveData.rest.getStatus = HttpFetchStatus.Loading;
        existingLiveData.rest.getError = null;
        existingLiveData.data = null;
      } else {
        state.live.push({
          id: action.meta.arg,
          loadTime: null,
          data: null,
          rest: { getStatus: HttpFetchStatus.Loading, getError: null },
        });
      }
    });
    builder.addCase(getFleetData.fulfilled, (state, action) => {
      const existingLiveData = state.live.find((l) => l.id === action.meta.arg);
      if (existingLiveData != null) {
        existingLiveData.rest.getStatus = HttpFetchStatus.Success;
        existingLiveData.rest.getError = null;
        existingLiveData.loadTime = action.payload.loadTime;
        existingLiveData.data = action.payload.data;
      }
    });
    builder.addCase(getFleetData.rejected, (state, action) => {
      const existingLiveData = state.live.find((l) => l.id === action.meta.arg);
      if (existingLiveData != null) {
        existingLiveData.rest.getStatus = HttpFetchStatus.Failed;
        existingLiveData.rest.getError = action.payload ?? null;
      }
    });

    builder.addCase(getFleetDataByFilters.pending, (state, action) =>
      startLiveQuery(state, action.meta.arg.datasetIds),
    );
    builder.addCase(getFleetDataByFilters.fulfilled, (state, action) =>
      finishLiveQuery(state, action.meta.arg.datasetIds, action.payload),
    );
    builder.addCase(getFleetDataByFilters.rejected, (state, action) =>
      failLiveQuery(state, action.meta.arg.datasetIds, action.payload),
    );
    builder.addCase(getInsights.pending, (state, action) =>
      startLiveQuery(state, action.meta.arg.key),
    );
    builder.addCase(getInsights.fulfilled, (state, action) =>
      finishLiveQuery(state, action.meta.arg.key, action.payload),
    );
    builder.addCase(getInsights.rejected, (state, action) =>
      failLiveQuery(state, action.meta.arg.key, action.payload),
    );

    builder.addCase(getFleetDataSets.pending, (state, action) => {
      state.datasets.rest.getStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(getFleetDataSets.fulfilled, (state, action) => {
      state.datasets.rest.getStatus = HttpFetchStatus.Success;
      state.datasets.data = action.payload;
    });
    builder.addCase(getFleetDataSets.rejected, (state, action) => {
      state.datasets.rest.getStatus = HttpFetchStatus.Failed;
      state.datasets.rest.getError = action.payload ?? null;
    });
    builder.addCase(createNewFleetDataSet.pending, (state, action) => {
      state.datasets.rest.postStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(createNewFleetDataSet.fulfilled, (state, action) => {
      state.datasets.rest.postStatus = HttpFetchStatus.Success;
      if (state.datasets.data != null) {
        state.datasets.data.datasets = [...state.datasets.data.datasets, action.payload];
      }
    });
    builder.addCase(createNewFleetDataSet.rejected, (state, action) => {
      state.datasets.rest.postStatus = HttpFetchStatus.Failed;
      state.datasets.rest.postError = action.payload ?? null;
    });
    builder.addCase(saveFleetDataSet.pending, (state, action) => {
      state.datasets.rest.patchStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(saveFleetDataSet.fulfilled, (state, action) => {
      state.datasets.rest.patchStatus = HttpFetchStatus.Success;
      if (state.datasets.data != null) {
        state.datasets.data.datasets = updateDataList(action.payload, state.datasets.data.datasets);
      }
      const liveDatasetIndex = state.live.findIndex((l) => l.id === action.payload.id);
      if (liveDatasetIndex !== -1) {
        state.live.splice(liveDatasetIndex, 1);
      }
    });
    builder.addCase(saveFleetDataSet.rejected, (state, action) => {
      state.datasets.rest.patchStatus = HttpFetchStatus.Failed;
      state.datasets.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteDataSetById.pending, (state, action) => {
      state.datasets.rest.deleteStatus = HttpFetchStatus.Loading;
    });
    builder.addCase(deleteDataSetById.fulfilled, (state, action) => {
      state.datasets.rest.deleteStatus = HttpFetchStatus.Success;

      const deletedDataSetIndex = state.datasets.data?.datasets?.findIndex(
        (d) => d.id === action.payload,
      );
      if (deletedDataSetIndex != null && deletedDataSetIndex !== -1) {
        state.datasets.data!.datasets.splice(deletedDataSetIndex, 1);
      }
      const liveDatasetIndex = state.live.findIndex((l) => l.id === action.payload);
      if (liveDatasetIndex !== -1) {
        state.live.splice(liveDatasetIndex, 1);
      }
      state.dashboards.data?.forEach((d) => {
        // remove widgets that use this dataset
        d.widgets = d.widgets.filter((w) => w.dataset !== action.payload);
      });
    });
    builder.addCase(deleteDataSetById.rejected, (state, action) => {
      state.datasets.rest.deleteStatus = HttpFetchStatus.Failed;
      state.datasets.rest.patchError = action.payload ?? null;
    });
  },
});

export const { setCurrentDashboardById, patchDashboardsRestState, patchDataSetsRestState } =
  fleetManagementSlice.actions;

export default fleetManagementSlice.reducer;
